import { computed, inject, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods } from '@ngrx/signals';
import {
  addEntity,
  EntityId,
  removeEntity,
  setAllEntities,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { MessageService } from 'primeng/api';
import { pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IUiError } from '../model';
import { Base, CreateForm, Page, SearchRequest } from '../model';
import { Crud } from '../services/crud-abstract';
import { modifyDates } from '../utils';
import { TableSignalState } from './table-signal-state';
import { withCommonMethods } from './common-methods.feature';

export function withCrudMethods<T extends Base<string>>(crudType: Type<Crud<T>>) {
  return signalStoreFeature(
    { state: type<TableSignalState<T>>(), },
    withEntities<T>(),
    withComputed(({ page, entities }) => ({
      data: computed(() => ({ content: entities(), page: page() }) as Page<T>),
    })),
    withCommonMethods(),
    withMethods((store) => {
      const service = inject(crudType);
      const messageService = inject(MessageService);
      return {
        search: rxMethod<SearchRequest>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap((request) =>
              service.search(request).pipe(
                tapResponse({
                  next: (rsp) =>
                    patchState(
                      store,
                      {
                        isLoading: false,
                        page: { ...rsp.page },
                      },
                      setAllEntities([...rsp.content])
                    ),
                  error: (err: IUiError) => patchState(store, { isLoading: false, error: err }),
                  finalize: () => patchState(store, { isLoading: false }),
                })
              )
            )
          )
        ),
        get: rxMethod<EntityId>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap((request) =>
              service.get(request).pipe(
                tapResponse({
                  next: (rsp) =>
                    patchState(
                      store,
                      {
                        isLoading: false,
                      },
                      setEntity(rsp)
                    ),
                  error: (err: IUiError) => patchState(store, { isLoading: false, error: err }),
                  finalize: () => patchState(store, { isLoading: false }),
                })
              )
            )
          )
        ),
        add: (item: T) => patchState(store, setEntity(item)),
        create: rxMethod<FormGroup<CreateForm<T>>>(
          pipe(
            tap((form) => {
              form.disable();
              patchState(store, { isLoading: true });
            }),
            switchMap((form) => {
              const data = form.getRawValue();
              modifyDates(data);
              return service.create(data).pipe(
                tapResponse({
                  next: (rsp) => {
                    patchState(store, addEntity(rsp));
                    messageService.add({
                      severity: 'success',
                      summary: 'Uspešno',
                      detail: `Uspešno kreiran`,
                    });
                  },
                  error: (err: IUiError) => {
                    patchState(store, { isLoading: false, error: err });
                    form.enable();
                  },
                  finalize: () => patchState(store, { isLoading: false }),
                })
              );
            })
          )
        ),
        delete: rxMethod<T>(
          pipe(
            switchMap((item) =>
              service.delete(item).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, removeEntity(item.id));
                    let reload = false;
                    patchState(store, (state) => {
                      let totalPages = 0;
                      let totalElements = 0;
                      let numberOfElements = 0;
                      if (state.page) {
                        const last = state.page.number === state.page.totalPages - 1;
                        const first = state.page.number === 0;
                        totalElements = state.page.totalElements;
                        numberOfElements = last
                          ? state.page.totalElements - state.page.number * state.page.size
                          : state.page.size;
                        totalPages = state.page.totalPages;
                        if (totalPages > 1 && !last) {
                          reload = true;
                        } else if (numberOfElements - 1 === 0) {
                          if (first) {
                            reload = true;
                          } else {
                            totalPages -= 1;
                          }
                        }
                      }

                      return {
                        page: state.page
                          ? {
                              ...state.page,
                              totalPages,
                              totalElements: totalElements - 1,
                            }
                          : undefined,
                      };
                    });
                  },
                  error: (err: IUiError) => patchState(store, { isLoading: false, error: err }),
                  finalize: () => patchState(store, { isLoading: false }),
                })
              )
            )
          )
        ),
      };
    })
  );
}
